import * as React from 'react';
import { Metadata, Schema } from '../../../types';
import { BlueCheck } from '../../Icon/icon';
import { useSelector } from '../../../utils/useSelector';
import { getSchema, parseDate } from '../../../utils';
import { NodeType } from '../../../enums/NodeType';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { DataType } from '../../../enums/DataType';
import { Pill } from '../../Pill/pill';

export interface CardProps {
  cardData: Metadata;
  highlight?: boolean;
  onClick: () => any;
  disabled?: boolean;
  pillText?: string;
  pillHoverText?: string;
}

export const Card = ({
  cardData,
  onClick,
  highlight,
  disabled,
  pillText,
  pillHoverText
}: Readonly<CardProps>) => {
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const selectedViewId = useSelector(s => s?.view?.selected?.props?.refId);
  const itemSchema = getSchema(schemas, NodeType.Item);
  if (!itemSchema?.props || !cardData?.props) return null;
  let { refId: id, title, itemId } = cardData.props;

  const fields = React.useMemo(
    () =>
      ['author', 'keyInitiative', 'status', 'publishedDate', 'contentType'].map(
        field => {
          const r = getCardField(cardData, itemSchema, schemas, field);
          if (field == 'contentType') {
            const s = getCardField(
              cardData,
              itemSchema,
              schemas,
              'contentSubtype'
            );
            if (s.value) {
              return {
                field,
                label: [r.label, s.label].join('/'),
                value: [r.value, s.value].join('/')
              };
            }
          }
          return r;
        }
      ),
    [cardData, itemSchema, schemas]
  );

  return (
    <div
      className={`search-result${highlight ? ' highlighted' : ''}${disabled ? ' disabled' : ''}${pillText ? ' has-pill' : ''}`}
      onClick={() => !disabled && onClick()}
    >
      {pillText && (
        <Pill
          name={pillText}
          title={pillHoverText}
          className="search-card-pill"
        />
      )}
      <div className="card-details">
        <div className="title">
          <a target="_blank" href={`?v=${selectedViewId}&id=${id}`}>
            {itemId ? (
              `(${itemId}) - ${title ? title : <i>Untitled </i>}`
            ) : title ? (
              title
            ) : (
              <i>Untitled</i>
            )}
          </a>
        </div>
        {fields.map(({ field, label, value }) => (
          <div key={field}>
            {label}: <span className="value">{value || '--'}</span>
          </div>
        ))}
      </div>
      {highlight && <BlueCheck />}
    </div>
  );
};

const getCardField = (
  cardData: Metadata,
  itemSchema: Schema,
  schemas: Schema[],
  field: string
) => {
  const f = findOne(itemSchema.props.fields, { field });
  if (!f) return null;
  const label = f.name;
  if (f.dataType == DataType.date) {
    return {
      field,
      label,
      value: parseDate(cardData.props[field])?.toLocaleDateString()
    };
  }
  const s = getSchema(schemas, f.dataType);
  const displayField = s.props?.displayField;
  const d = cardData.props[field];
  const value = !d?.length ? null : d[0].props[displayField];
  return { field, label, value };
};
